import type { ReactNode } from 'react';
import { createContext, useContext } from 'react';

import { invariant } from '#/utils/invariant';

export type SubmitInterceptor = () => void | Promise<void>;
export type SubmittedInterceptor = () => void | Promise<void>;

type UnsafeAny = ReturnType<typeof JSON.parse>;

export type FormErrorData<TKey> = {
  location: string;
  name: TKey | '';
  description: string;
  type?: string;
  data?: Record<string, UnsafeAny>;
};

export type FormErrorResponse<TKey> =
  | {
      responseJson?: {
        status: 'error';
        errors: FormErrorData<TKey>[];
      };
    }
  | Error;

export type FormMapErrorFunction<TValues extends Record<string, UnsafeAny>> = (
  data: FormErrorData<keyof TValues> & {
    value: TValues[keyof TValues] | undefined;
  },
) => ReactNode;

export type FormContextValue<TValues extends Record<string, UnsafeAny>> = {
  values: TValues;
  getValue: <TKey extends keyof TValues>(key: keyof TValues) => TValues[TKey];
  updateValue: <TKey extends keyof TValues>(
    name: TKey,
    value: TValues[TKey],
  ) => void;
  updateValues: (update: Partial<TValues>) => void;
  updateValueWith: <TKey extends keyof TValues>(
    name: TKey | TKey[],
    updater: (...args: UnsafeAny[]) => UnsafeAny,
  ) => void;
  clearValues: () => void;
  fetchingAddress: boolean;
  setFetchingAddress: (fetching: boolean) => void;
  errors: APIErrors;
  setErrors: (apiErrors: APIErrors, append?: boolean) => void;
  clearErrors: (errors?: string[]) => void;
  submitting: boolean;
  updateSubmitting: (submitting: boolean) => void;
  updateEdited: (edited: boolean) => void;
  submitDisabled: boolean;
  submitForm: (values?: Partial<TValues>) => Promise<void>;
  updateSubmitDisabled: (submitDisabled: boolean) => void;
  fieldQaIdPrefix: string | null | undefined;
  edited: boolean;
  handleError: (
    error: FormErrorResponse<keyof TValues>,
    updatedFormData?: Partial<TValues> | null,
  ) => void;
  addSubmitInterceptor: (interceptor: SubmitInterceptor) => () => void;
  addSubmittedInterceptor: (interceptor: SubmittedInterceptor) => () => void;
  registerMountedField: (name: string) => () => void;
  isFieldMounted: (name: string) => boolean;
};

export type FormSubmitFunctionData<TValues extends Record<string, UnsafeAny>> =
  {
    values: TValues;
    updateValues: FormContextValue<TValues>['updateValues'];
    updateValueWith: FormContextValue<TValues>['updateValueWith'];
    updateSubmitting: FormContextValue<TValues>['updateSubmitting'];
    updateEdited: FormContextValue<TValues>['updateEdited'];
    handleError: FormContextValue<TValues>['handleError'];
    clearErrors: FormContextValue<TValues>['clearErrors'];
    clearValues: FormContextValue<TValues>['clearValues'];
    setErrors: FormContextValue<TValues>['setErrors'];
    setFetchingAddress: FormContextValue<TValues>['setFetchingAddress'];
  };

export type FormSubmitFunction<
  TValues extends Record<string, UnsafeAny>,
  TReturn = unknown,
> = (data: FormSubmitFunctionData<TValues>) => TReturn | Promise<TReturn>;

export type FormRenderProps<TValues extends Record<string, UnsafeAny>> = {
  values: TValues;
  getValue: FormContextValue<TValues>['getValue'];
  updateValue: FormContextValue<TValues>['updateValue'];
  updateValueWith: FormContextValue<TValues>['updateValueWith'];
  updateValues: FormContextValue<TValues>['updateValues'];
  clearValues: FormContextValue<TValues>['clearValues'];
  hasErrors: boolean;
  setErrors: FormContextValue<TValues>['setErrors'];
  submitting: boolean;
  submitDisabled: boolean;
  updateSubmitDisabled: FormContextValue<TValues>['updateSubmitDisabled'];
  errors: FormContextValue<TValues>['errors'];
  submitForm: (values?: Partial<TValues>) => Promise<void>;
  edited: boolean;
  handleError: FormContextValue<TValues>['handleError'];
  clearErrors: FormContextValue<TValues>['clearErrors'];
  fieldQaIdPrefix: string | null | undefined;
};

export const FormContext = createContext({} as FormContextValue<UnsafeAny>);

export function FormContextProvider<TValues extends Record<string, UnsafeAny>>({
  value,
  children,
}: {
  value: FormContextValue<TValues>;
  children?: ReactNode;
}) {
  return (
    <FormContext.Provider value={value as FormContextValue<UnsafeAny>}>
      {children}
    </FormContext.Provider>
  );
}

export function FormContextConsumer<TValues extends Record<string, UnsafeAny>>({
  children,
}: {
  children: (contextValue: FormContextValue<TValues>) => ReactNode;
}) {
  return (
    <FormContext.Consumer>
      {(value) => {
        invariant(value, 'Missing FormContext value');
        return children(value as FormContextValue<TValues>);
      }}
    </FormContext.Consumer>
  );
}

export const useFormContext = <
  TValues extends Record<string, UnsafeAny> = Record<string, UnsafeAny>,
>() => useContext(FormContext) as unknown as FormContextValue<TValues>;
